import { useEffect, useMemo, useRef, useState } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/auth/authStore';
import { getSocket } from '@/net/socket';
import { Avatar } from '@/art';
import { BackButton } from '@/components/BackButton';
import { useT } from '@/i18n/useI18n';
import {
  socialApi,
  type DmMessage,
  type FriendEntry,
  type GlobalMessage,
} from '@/social/socialApi';
import { challengeApi, type FriendChallengeDto } from '@/challenges/challengeApi';

type SubTab = 'friends' | 'challenges' | 'global';

export function FriendsScreen() {
  const t = useT();
  const go = useUIStore((s) => s.go);
  const me = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<SubTab>('friends');
  const [openDm, setOpenDm] = useState<FriendEntry | null>(null);

  // Friend list state shared across both tabs (used for online dots in chat too)
  const [friends, setFriends] = useState<FriendEntry[]>([]);
  const [incoming, setIncoming] = useState<FriendEntry[]>([]);
  const [outgoing, setOutgoing] = useState<FriendEntry[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const refresh = () =>
    socialApi
      .list()
      .then((data) => {
        setFriends(data.friends);
        setIncoming(data.incoming);
        setOutgoing(data.outgoing);
        setErr(null);
      })
      .catch((e) => setErr(String(e)));

  useEffect(() => {
    void refresh();
  }, []);

  // Live presence updates — flip the `online` flag for any friend on the list.
  useEffect(() => {
    const socket = getSocket();
    const onPresence = (p: { userId: string; online: boolean }) => {
      setFriends((prev) =>
        prev.map((f) => (f.userId === p.userId ? { ...f, online: p.online } : f)),
      );
    };
    socket.on('presence:update', onPresence);
    return () => {
      socket.off('presence:update', onPresence);
    };
  }, []);

  return (
    <div className="screen">
      <div className="hex-bg" />

      <div style={{ padding: '14px 18px 0' }}>
        <BackButton onClick={() => go('home')} />
        <div className="display" style={{ fontSize: 26, marginTop: 10 }}>
          {t('friends.title')}
        </div>
      </div>

      <div style={{ padding: '12px 18px' }}>
        <div
          style={{
            display: 'flex',
            borderRadius: 10,
            padding: 3,
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid var(--hairline)',
          }}
        >
          {(
            [
              { id: 'friends', label: `${t('friends.tab_friends')} ${friends.length || ''}` },
              { id: 'challenges', label: t('friends.tab_challenges') },
              { id: 'global', label: t('friends.tab_global') },
            ] as const
          ).map((k) => (
            <div
              key={k.id}
              onClick={() => setTab(k.id)}
              style={{
                flex: 1,
                padding: 8,
                textAlign: 'center',
                cursor: 'pointer',
                borderRadius: 8,
                fontFamily: 'var(--f-display)',
                fontSize: 11,
                letterSpacing: '0.08em',
                background: tab === k.id ? 'var(--c-yellow)' : 'transparent',
                color: tab === k.id ? '#1E1B4B' : 'var(--ink-muted)',
              }}
            >
              {k.label}
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {tab === 'friends' && (
          <FriendsTab
            friends={friends}
            incoming={incoming}
            outgoing={outgoing}
            err={err}
            onRefresh={refresh}
            onOpenDm={setOpenDm}
          />
        )}
        {tab === 'global' && <GlobalChatTab onlineUserIds={friends.filter((f) => f.online).map((f) => f.userId)} />}
        {tab === 'challenges' && <ChallengesTab />}
      </div>

      {openDm && me && (
        <DmModal friend={openDm} meId={me.id} onClose={() => setOpenDm(null)} />
      )}

    </div>
  );
}

// ── Friends tab ─────────────────────────────────────────────────────

function FriendsTab({
  friends,
  incoming,
  outgoing,
  err,
  onRefresh,
  onOpenDm,
}: {
  friends: FriendEntry[];
  incoming: FriendEntry[];
  outgoing: FriendEntry[];
  err: string | null;
  onRefresh: () => void;
  onOpenDm: (f: FriendEntry) => void;
}) {
  const t = useT();
  const [search, setSearch] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addMsg, setAddMsg] = useState<string | null>(null);
  const [challengeBusy, setChallengeBusy] = useState<string | null>(null);
  const go = useUIStore((state) => state.go);

  const onlineFriends = friends.filter((f) => f.online);
  const offlineFriends = friends.filter((f) => !f.online);

  const onAdd = async () => {
    const name = search.trim();
    if (!name || addBusy) return;
    setAddBusy(true);
    setAddMsg(null);
    try {
      const r = await socialApi.requestByUsername(name);
      setAddMsg(r.accepted ? t('friends.friend_added') : t('friends.request_sent'));
      setSearch('');
      await onRefresh();
    } catch (e) {
      setAddMsg(String(e));
    } finally {
      setAddBusy(false);
      window.setTimeout(() => setAddMsg(null), 2500);
    }
  };

  const onChallenge = async (friend: FriendEntry) => {
    if (challengeBusy) return;
    setChallengeBusy(friend.userId);
    setAddMsg(null);
    try {
      const { challenge } = await challengeApi.create({ targetUserId: friend.userId });
      go('friendChallenge', challenge.code);
    } catch (reason: unknown) {
      setAddMsg(reason instanceof Error && reason.message === 'NO_COMPLETED_WIN'
        ? t('challenge.need_win')
        : t('challenge.error_default'));
    } finally {
      setChallengeBusy(null);
    }
  };

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '0 18px 80px' }}>
      <div
        style={{ display: 'flex', gap: 6, marginBottom: 14 }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void onAdd();
          }}
          placeholder={t('friends.add_placeholder')}
          style={{
            flex: 1,
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid var(--hairline)',
            borderRadius: 8,
            padding: '8px 10px',
            color: 'var(--ink)',
            fontSize: 13,
            outline: 'none',
            minWidth: 0,
          }}
        />
        <button
          onClick={onAdd}
          disabled={addBusy || !search.trim()}
          className="btn btn-ghost"
          style={{
            fontSize: 11,
            padding: '6px 14px',
            color: 'var(--c-yellow)',
            borderColor: 'rgba(244,199,36,0.4)',
            opacity: addBusy || !search.trim() ? 0.4 : 1,
          }}
        >
          {t('friends.add_btn')}
        </button>
      </div>

      {addMsg && (
        <div
          style={{
            marginBottom: 10,
            padding: '6px 10px',
            fontSize: 11,
            color: addMsg.includes('Error') || addMsg.includes('NOT_FOUND') ? 'var(--c-red)' : 'var(--c-cyan)',
            background: 'rgba(34,211,238,0.08)',
            border: '1px solid rgba(34,211,238,0.3)',
            borderRadius: 8,
          }}
        >
          {addMsg}
        </div>
      )}

      {err && (
        <div
          style={{
            padding: 10,
            color: 'var(--c-red)',
            fontSize: 12,
            background: 'rgba(230,57,70,0.16)',
            borderRadius: 8,
            marginBottom: 10,
          }}
        >
          {err}
        </div>
      )}

      {incoming.length > 0 && (
        <Section title={t('friends.incoming_requests', { n: incoming.length })} accent="var(--c-yellow)">
          {incoming.map((f) => (
            <FriendRow
              key={f.userId}
              friend={f}
              actions={
                <>
                  <button
                    className="btn btn-ghost"
                    onClick={() => socialApi.accept(f.userId).then(onRefresh)}
                    style={{
                      fontSize: 10,
                      padding: '5px 10px',
                      color: 'var(--c-cyan)',
                      borderColor: 'rgba(34,211,238,0.4)',
                    }}
                  >
                    {t('friends.accept')}
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={() => socialApi.remove(f.userId).then(onRefresh)}
                    style={{
                      fontSize: 10,
                      padding: '5px 10px',
                      color: 'var(--c-red)',
                      borderColor: 'rgba(230,57,70,0.4)',
                    }}
                  >
                    {t('friends.decline')}
                  </button>
                </>
              }
            />
          ))}
        </Section>
      )}

      {outgoing.length > 0 && (
        <Section title={t('friends.pending_sent', { n: outgoing.length })}>
          {outgoing.map((f) => (
            <FriendRow
              key={f.userId}
              friend={f}
              dim
              actions={
                <button
                  className="btn btn-ghost"
                  onClick={() => socialApi.remove(f.userId).then(onRefresh)}
                  style={{ fontSize: 10, padding: '5px 10px' }}
                >
                  {t('friends.cancel')}
                </button>
              }
            />
          ))}
        </Section>
      )}

      <Section title={t('friends.online_count', { n: onlineFriends.length })} accent="var(--c-cyan)">
        {onlineFriends.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--ink-dim)', padding: 8 }}>
            {t('friends.none_online')}
          </div>
        ) : (
          onlineFriends.map((f) => (
            <FriendRow
              key={f.userId}
              friend={f}
              onChat={() => onOpenDm(f)}
              actions={<ChallengeButton busy={challengeBusy === f.userId} onClick={() => void onChallenge(f)} />}
            />
          ))
        )}
      </Section>

      {offlineFriends.length > 0 && (
        <Section title={t('friends.offline_count', { n: offlineFriends.length })}>
          {offlineFriends.map((f) => (
            <FriendRow
              key={f.userId}
              friend={f}
              dim
              onChat={() => onOpenDm(f)}
              actions={<ChallengeButton busy={challengeBusy === f.userId} onClick={() => void onChallenge(f)} />}
            />
          ))}
        </Section>
      )}

      {friends.length === 0 && incoming.length === 0 && outgoing.length === 0 && (
        <div
          style={{
            padding: 30,
            textAlign: 'center',
            color: 'var(--ink-muted)',
            fontSize: 12,
          }}
        >
          {t('friends.no_friends')}
        </div>
      )}
    </div>
  );
}

function ChallengeButton({ busy, onClick }: { busy: boolean; onClick: () => void }) {
  const t = useT();
  return (
    <button
      className="btn btn-ghost"
      disabled={busy}
      onClick={(event) => { event.stopPropagation(); onClick(); }}
      style={{ fontSize: 9, padding: '5px 8px', color: 'var(--c-yellow)', borderColor: 'rgba(244,199,36,.38)' }}
    >
      {busy ? '…' : `⚔ ${t('challenge.short')}`}
    </button>
  );
}

function ChallengesTab() {
  const t = useT();
  const go = useUIStore((state) => state.go);
  const [items, setItems] = useState<FriendChallengeDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    challengeApi.list()
      .then(({ challenges }) => setItems(challenges))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-muted)' }}>{t('common.loading')}</div>;
  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: '0 18px 90px' }}>
      <div style={{ color: 'var(--ink-muted)', fontSize: 11, lineHeight: 1.5, marginBottom: 12 }}>
        {t('challenge.list_hint')}
      </div>
      {items.length === 0 && (
        <div className="card" style={{ padding: 22, textAlign: 'center', color: 'var(--ink-dim)', fontSize: 12 }}>
          {t('challenge.empty')}
        </div>
      )}
      <div style={{ display: 'grid', gap: 8 }}>
        {items.map((item) => {
          const peer = item.mine ? item.target : item.creator;
          return (
            <button
              key={item.code}
              onClick={() => go('friendChallenge', item.code)}
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: 10,
                alignItems: 'center',
                padding: '12px 14px',
                borderRadius: 12,
                border: `1px solid ${item.canPlay ? 'rgba(244,199,36,.55)' : 'var(--hairline)'}`,
                background: item.canPlay ? 'rgba(244,199,36,.09)' : 'rgba(0,0,0,.3)',
                color: 'var(--ink)',
                textAlign: 'left',
              }}
            >
              <span>
                <span className="display" style={{ display: 'block', fontSize: 13 }}>
                  {peer?.name ?? t('challenge.open_invite')}
                </span>
                <span style={{ display: 'block', marginTop: 3, color: 'var(--ink-dim)', fontSize: 10 }}>
                  {item.mine ? t('challenge.sent') : t('challenge.received')} · {item.status.toUpperCase()}
                </span>
              </span>
              <span className="display" style={{ color: 'var(--c-yellow)', fontSize: 16 }}>
                {(item.targetDurationMs / 1000).toFixed(2)}s
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
  accent,
}: {
  title: string;
  children: React.ReactNode;
  accent?: string;
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        className="tag"
        style={{ marginBottom: 6, color: accent ?? 'var(--ink-dim)' }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  );
}

function FriendRow({
  friend,
  actions,
  onChat,
  dim,
}: {
  friend: FriendEntry;
  actions?: React.ReactNode;
  onChat?: () => void;
  dim?: boolean;
}) {
  const t = useT();
  return (
    <div
      onClick={onChat}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        background: 'rgba(0,0,0,0.3)',
        border: '1px solid var(--hairline)',
        borderRadius: 10,
        cursor: onChat ? 'pointer' : 'default',
        opacity: dim ? 0.7 : 1,
      }}
    >
      <div style={{ position: 'relative' }}>
        <Avatar color={friend.avatarColor} size={36} photoUrl={friend.avatarUrl} characterUrl={friend.characterIdleUrl} ring={false} />
        <div
          style={{
            position: 'absolute',
            bottom: -1,
            right: -1,
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: friend.online ? 'var(--c-cyan)' : 'var(--ink-dim)',
            border: '2px solid var(--bg-deep)',
            boxShadow: friend.online ? '0 0 8px rgba(34,211,238,0.8)' : 'none',
          }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="display" style={{ fontSize: 13 }}>
          {friend.displayName}
        </div>
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-dim)' }}>
          {t('friends.rank_mmr', { rank: friend.rank.toUpperCase(), mmr: friend.mmr })}
        </div>
      </div>
      {actions && <div style={{ display: 'flex', gap: 6 }}>{actions}</div>}
    </div>
  );
}

// ── Global chat ─────────────────────────────────────────────────────

function GlobalChatTab({ onlineUserIds }: { onlineUserIds: string[] }) {
  const t = useT();
  const me = useAuthStore((s) => s.user);
  const [messages, setMessages] = useState<GlobalMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    socialApi.globalHistory().then((d) => setMessages(d.messages)).catch(() => {});
  }, []);

  useEffect(() => {
    const socket = getSocket();
    const onMsg = (msg: GlobalMessage) => {
      setMessages((prev) => [...prev, msg].slice(-200));
    };
    socket.on('chat:global', onMsg);
    return () => {
      socket.off('chat:global', onMsg);
    };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  const send = () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    // Realtime path — socket broadcasts to all including us.
    const socket = getSocket();
    socket.emit('chat:global', { text });
    setDraft('');
    setBusy(false);
  };

  void onlineUserIds;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0 18px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {messages.length === 0 && (
          <div
            style={{
              padding: 30,
              textAlign: 'center',
              color: 'var(--ink-muted)',
              fontSize: 12,
            }}
          >
            {t('friends.no_messages')}
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender.id === me?.id;
          return (
            <div
              key={m.id}
              style={{
                display: 'flex',
                gap: 8,
                flexDirection: mine ? 'row-reverse' : 'row',
                alignItems: 'flex-end',
              }}
            >
              {!mine && (
                <Avatar
                  color={m.sender.avatarColor}
                  size={28}
                  ring={false}
                  photoUrl={m.sender.avatarUrl}
                  characterUrl={m.sender.characterIdleUrl}
                />
              )}
              <div
                style={{
                  maxWidth: '72%',
                  padding: '6px 10px',
                  borderRadius: 10,
                  background: mine ? 'rgba(244,199,36,0.18)' : 'rgba(0,0,0,0.4)',
                  border: mine ? '1px solid rgba(244,199,36,0.4)' : '1px solid var(--hairline)',
                }}
              >
                {!mine && (
                  <div
                    className="mono"
                    style={{ fontSize: 9, color: 'var(--ink-dim)', marginBottom: 2 }}
                  >
                    {m.sender.displayName}
                  </div>
                )}
                <div style={{ fontSize: 13, color: 'var(--ink)', wordWrap: 'break-word' }}>
                  {m.text}
                </div>
                <div
                  className="mono"
                  style={{
                    fontSize: 9,
                    color: 'var(--ink-dim)',
                    marginTop: 2,
                    textAlign: 'right',
                  }}
                >
                  {new Date(m.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: '10px 18px',
          borderTop: '1px solid var(--hairline)',
          background: 'rgba(0,0,0,0.45)',
        }}
      >
        <input
          value={draft}
          maxLength={280}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') send();
          }}
          placeholder={t('friends.say_something')}
          style={{
            flex: 1,
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid var(--hairline)',
            borderRadius: 8,
            padding: '8px 10px',
            color: 'var(--ink)',
            fontSize: 13,
            outline: 'none',
            minWidth: 0,
          }}
        />
        <button
          onClick={send}
          disabled={!draft.trim() || busy}
          className="btn btn-primary"
          style={{
            fontSize: 11,
            padding: '8px 14px',
            opacity: !draft.trim() || busy ? 0.4 : 1,
          }}
        >
          {t('friends.send')}
        </button>
      </div>
    </div>
  );
}

// ── DM modal ─────────────────────────────────────────────────────────

function DmModal({
  friend,
  meId,
  onClose,
}: {
  friend: FriendEntry;
  meId: string;
  onClose: () => void;
}) {
  const t = useT();
  const [messages, setMessages] = useState<DmMessage[]>([]);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    socialApi.dmHistory(friend.userId).then((d) => setMessages(d.messages)).catch(() => {});
  }, [friend.userId]);

  useEffect(() => {
    const socket = getSocket();
    const onMsg = (msg: DmMessage) => {
      const involvesUs =
        (msg.fromId === meId && msg.toId === friend.userId) ||
        (msg.fromId === friend.userId && msg.toId === meId);
      if (!involvesUs) return;
      setMessages((prev) => [...prev, msg]);
    };
    socket.on('chat:dm', onMsg);
    return () => {
      socket.off('chat:dm', onMsg);
    };
  }, [friend.userId, meId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    const socket = getSocket();
    socket.emit('chat:dm', { toId: friend.userId, text });
    setDraft('');
  };

  // Group consecutive DMs from the same side into bubbles (modern chat UX).
  const grouped = useMemo(() => {
    return messages;
  }, [messages]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: 'var(--bg-deep)',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="hex-bg" style={{ position: 'absolute', inset: 0 }} />
      <div
        style={{
          padding: '14px 18px 10px',
          borderBottom: '1px solid var(--hairline)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          position: 'relative',
          background: 'rgba(0,0,0,0.5)',
        }}
      >
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--ink-muted)',
            fontSize: 22,
            cursor: 'pointer',
            padding: 0,
            marginRight: 4,
          }}
          aria-label={t('common.back')}
        >
          ‹
        </button>
        <Avatar color={friend.avatarColor} size={38} ring={false} photoUrl={friend.avatarUrl} characterUrl={friend.characterIdleUrl} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="display" style={{ fontSize: 14 }}>
            {friend.displayName}
          </div>
          <div
            className="mono"
            style={{
              fontSize: 10,
              color: friend.online ? 'var(--c-cyan)' : 'var(--ink-dim)',
            }}
          >
            {friend.online ? `● ${t('common.online')}` : `○ ${t('common.offline')}`} · {friend.rank.toUpperCase()}
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '14px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          position: 'relative',
          zIndex: 1,
        }}
      >
        {grouped.length === 0 && (
          <div
            style={{
              padding: 30,
              textAlign: 'center',
              color: 'var(--ink-muted)',
              fontSize: 12,
            }}
          >
            {t('friends.start_conversation')}
          </div>
        )}
        {grouped.map((m) => {
          const mine = m.fromId === meId;
          return (
            <div
              key={m.id}
              style={{
                display: 'flex',
                justifyContent: mine ? 'flex-end' : 'flex-start',
              }}
            >
              <div
                style={{
                  maxWidth: '78%',
                  padding: '8px 12px',
                  borderRadius: 12,
                  background: mine ? 'rgba(34,211,238,0.18)' : 'rgba(255,255,255,0.06)',
                  border: mine
                    ? '1px solid rgba(34,211,238,0.4)'
                    : '1px solid var(--hairline)',
                }}
              >
                <div style={{ fontSize: 13, color: 'var(--ink)', wordWrap: 'break-word' }}>
                  {m.text}
                </div>
                <div
                  className="mono"
                  style={{
                    fontSize: 9,
                    color: 'var(--ink-dim)',
                    marginTop: 2,
                    textAlign: 'right',
                  }}
                >
                  {new Date(m.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: '10px 18px 16px',
          borderTop: '1px solid var(--hairline)',
          background: 'rgba(0,0,0,0.55)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <input
          value={draft}
          maxLength={280}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') send();
          }}
          placeholder={t('friends.message_placeholder', { name: friend.displayName })}
          style={{
            flex: 1,
            background: 'rgba(0,0,0,0.35)',
            border: '1px solid var(--hairline)',
            borderRadius: 8,
            padding: '8px 10px',
            color: 'var(--ink)',
            fontSize: 13,
            outline: 'none',
            minWidth: 0,
          }}
        />
        <button
          onClick={send}
          disabled={!draft.trim()}
          className="btn btn-primary"
          style={{
            fontSize: 11,
            padding: '8px 14px',
            opacity: !draft.trim() ? 0.4 : 1,
          }}
        >
          {t('friends.send')}
        </button>
      </div>
    </div>
  );
}
